import React, { Fragment } from 'react';
import classnames from 'classnames';
import { noop, find, get, isEmpty, isArray, includes, head } from 'lodash';

// local dependencies
import { searchInput as SearchInput } from 'ui/views/generic/searchInput';
import { table as Table } from 'modules/table';
import { getLabel } from 'utils/getLabel';
import { OutsideClickHandler } from 'vendors/OutsideClickHandler';
import { isVisible } from 'resolvers/getVisibleEntities';

import { isInbound, getCallerTypeFromName } from 'resolvers/callStateEntities';

import { TABLE_TEMPLATE_TYPE } from 'constants/common';

import { button as Button } from 'modules/button';
import { toggleField as ToggleField } from 'ui/views/generic/toggleField';
import { inputField as InputField } from 'ui/views/generic/inputField';
import { dateField as DateField } from 'ui/views/generic/dateField';
import { externalLinksResolverResolver } from 'resolvers/externalLinksResolver';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { tableSearchFlyoutWidgetWithAdvancedOptions as TableSearchFlyoutWidgetWithAdvancedOptions } from 'ui/views/widgets/tableSearchFlyoutWidgetWithAdvancedOptions';
 *
 * // render
 * <TableSearchFlyoutWidgetWithAdvancedOptions propName='propValue'/>
 */
export const tableSearchFlyoutWidgetWithAdvancedOptions = ( props ) => {

    // get search filter state and config
    const searchFilter = find( props.filters, { name: 'search' } );
    const infoFilter = get( searchFilter, 'config.info', {} );
    const searchQuery = get( searchFilter, 'value', '' );

    const advancedSearchFilter = get( searchFilter, 'config.advancedOptions', {} );
    const advancedSearchConfig = get( props, `sortAndFilter.${ advancedSearchFilter.name }`, {} );

    const callState= get(props, 'callState', {});
   // const externalLinks = !isEmpty(callState)  && callState.callerType==='1'? get( searchFilter, 'config.externalLink', {} ): isEmpty(callState) && callState==undefined ? get( searchFilter, 'config.externalLink', {} ):null;
   // const externalLinks = get( searchFilter, 'config.externalLink', {} );
  // const callerType = getCallerTypeFromName( callState.callerType );
   const externalLinks = !isEmpty(callState) && ( ( callState.callType=='inbound' &&  callState.callerType=='thirdparty') || ( callState.callType=='outbound' &&  callState.callerType=='pharmacy' ) ) ?isEmpty(callState) && callState==undefined ? get( searchFilter, 'config.externalLink', {} ):null: get( searchFilter, 'config.externalLink', {} );
    // ui configurations
    const borderlessSearch = get( props.layoutConfig, 'styles.borderlessSearch', true );
    const inlineTableView = get( props.layoutConfig, 'styles.inlineTableView', false );
    const expandedTableView = get( props.layoutConfig, 'styles.expandedTableView', false );
    const searchPattern = get( props.layoutConfig, 'inputConfig.searchPattern', null );

    // dynamic widget class name
    const widgetClassName = classnames( 'ui-table-search-flyout-widget-with-advanced-options', props.className );

    // table container class name
    const tableContainerClassName = classnames( 'ui-table-search-flyout-widget-with-advanced-options__table-container', {
        'ui-table-search-flyout-widget-with-advanced-options__table-container--inline': inlineTableView,
        'ui-table-search-flyout-widget-with-advanced-options__table-container--expanded': expandedTableView,
    } );

    const focus = () => {
        if ( !isEmpty( infoFilter ) && isVisible( infoFilter, props.visibility ) ) {
            return (
                <div className='ui-table-search-flyout-widget-with-advanced-options__custom-tooltip'
                    dangerouslySetInnerHTML={{ __html: `<ul style='list-style: disc'>${getLabel( infoFilter.message )}</ul>` }} >
                </div>
            );
        }
    };

    // shared identity-fields inputs (First Name / Last Name / Date of Birth), reused by both
    // the MPID advanced-search panel and the Name & DOB search mode below -- same components,
    // same `onSortAndFilter( { type: 'memberIdentity', ... } )` dispatch pattern (PBI 107807)
    const renderIdentityFields = ( { includeDateOfBirth, required } ) => (
        <div className='ui-table-search-flyout-widget-with-advanced-options__identity-fields'>
            <InputField
                className='ui-table-search-flyout-widget-with-advanced-options__identity-fields__field'
                name='firstName'
                label={{ value: required ? 'First Name' : 'First Name (optional)' }}
                required={ required }
                value={ get( props.customizedRequestData, 'firstName', '' ) }
                onChange={ ( { value } ) => props.onSortAndFilter( { type: 'memberIdentity', name: 'firstName', value } ) }
            />
            <InputField
                className='ui-table-search-flyout-widget-with-advanced-options__identity-fields__field'
                name='lastName'
                label={{ value: required ? 'Last Name' : 'Last Name (optional)' }}
                required={ required }
                value={ get( props.customizedRequestData, 'lastName', '' ) }
                onChange={ ( { value } ) => props.onSortAndFilter( { type: 'memberIdentity', name: 'lastName', value } ) }
            />
            { includeDateOfBirth ? <DateField
                className='ui-table-search-flyout-widget-with-advanced-options__identity-fields__field'
                name='dateOfBirth'
                label={{ value: 'Date of Birth' }}
                placeholder={{ value: 'Date of Birth' }}
                required={ required }
                value={ get( props.customizedRequestData, 'dateOfBirth', '' ) }
                onChange={ ( { value } ) => props.onSortAndFilter( { type: 'memberIdentity', name: 'dateOfBirth', value } ) }
            /> : null }
        </div>
    );

    const searchPlaceholder = advancedSearchConfig.usingAdvancedSearch ?
                              get( find( advancedSearchFilter.placeholders, { name: head( advancedSearchConfig.selected ) } ), 'placeholder',  {} ) :
                              searchFilter.placeholder;

    return (
        <OutsideClickHandler className={widgetClassName} disabled={inlineTableView} onOutsideClick={() => props.onFilterChange( { name: 'search', value: '' } )}>
            {/* search input */}
            {
                searchFilter ? (
                    <SearchInput
                        className='ui-table-search-flyout-widget-with-advanced-options__search'
                        loading={props.isFetching && 'filters' === props.fetchingType}
                        value={searchQuery}
                        placeholder={ getLabel( searchPlaceholder )}
                        onChange={( value ) => props.onFilterChange( { name: 'search', value } )}
                        searchPattern={searchPattern}
                        info={ focus()}
                        activateTooltip={isVisible( infoFilter, props.visibility )}
                        borderless={borderlessSearch}
                    />
                ) : null
            }
            {/* advanced search section */}
            {
                isVisible( advancedSearchFilter, props.visibility ) ? <div className='ui-table-search-flyout-widget-with-advanced-options__advanced-search' >
                    <div className='ui-table-search-flyout-widget-with-advanced-options__advanced-search__text'>
                        { getLabel( advancedSearchFilter.title ) }
                    </div>

                    <ToggleField
                        onChange = { () => props.onSortAndFilter( { type: 'toggleAdvancedSearch', name: advancedSearchFilter.name, value: undefined, list: undefined } )}
                        className = { 'ui-table-search-flyout-widget-with-advanced-options__advanced-search__toggle' }
                        height = { 28 }
                        width = { 50 }
                        value = { get( advancedSearchConfig, 'usingAdvancedSearch', false ) }
                    />

                    <div className='ui-table-search-flyout-widget-with-advanced-options__advanced-search__options'>
                        {
                            isArray( advancedSearchFilter.options ) && get( advancedSearchConfig, 'usingAdvancedSearch', false ) && advancedSearchFilter.options.map( ( singleSelectOption ) => {
                                const visibileCheck = get(props.visibility, singleSelectOption.value, false)
                                
                                return(
                                    visibileCheck ?
                                    <div key={ singleSelectOption.value } className='ui-table-search-flyout-widget-with-advanced-options__advanced-search__options__option' >
                                      
                                        <Button
                                            className='ui-table-search-flyout-widget-with-advanced-options__advanced-search__options__option__button'
                                            title={ singleSelectOption.title }
                                            size='small'
                                            showIcon={ false }
                                            onClick={ () => props.onSortAndFilter( { type: TABLE_TEMPLATE_TYPE.ADVANCED_SEARCH, name: advancedSearchFilter.name, value: singleSelectOption.value, list: undefined } ) }
                                            outline = { !includes( advancedSearchConfig.selected, singleSelectOption.value ) }
                                        />
                                    </div>:null
                                );
                            } )
                        }
                    </div>
                    {
                        get( advancedSearchConfig, 'usingAdvancedSearch', false ) && includes( advancedSearchConfig.selected, 'memberId' ) ?
                            renderIdentityFields( { includeDateOfBirth: false, required: false } ) : null
                    }
                    {/* Name & DOB search entry point (PBI 107807 fix-wave F): a real, discoverable
                        way to trigger the backend's identity-only live fallback. It is kept
                        mutually exclusive with, and separate from, the MPID advanced-search toggle
                        above -- entering it does NOT set `usingAdvancedSearch`, so the request
                        keeps routing to the backend's `defaultSearch()`, the only method with the
                        name+DOB live fallback wired in (see `TableSearchWidget.container.jsx` for
                        the routing rationale). Only shown while the MPID advanced-search toggle is
                        off, since the two modes cannot both be active. */}
                    {
                        props.identitySearchEnabled && ! get( advancedSearchConfig, 'usingAdvancedSearch', false ) ? (
                            <div className='ui-table-search-flyout-widget-with-advanced-options__identity-search'>
                                <Button
                                    className='ui-table-search-flyout-widget-with-advanced-options__identity-search__toggle'
                                    title={{ value: props.isIdentitySearchActive ? 'Hide Name & DOB Search' : 'Search by Name & DOB' }}
                                    size='small'
                                    showIcon={ false }
                                    onClick={ () => props.onSortAndFilter( { type: 'toggleIdentitySearch', name: advancedSearchFilter.name, value: undefined, list: undefined } ) }
                                    outline={ ! props.isIdentitySearchActive }
                                />
                                { props.isIdentitySearchActive ? renderIdentityFields( { includeDateOfBirth: true, required: true } ) : null }
                            </div>
                        ) : null
                    }
                    {
                        !isEmpty( externalLinks ) && isVisible( externalLinks, props.visibility ) ?
                        <div className='ui-table-search-flyout-widget-with-advanced-options__external-link' >
                            
                            {
                                isArray( get( externalLinks, 'options', [] ) ) && get( externalLinks, 'options', [] ).map( ( links, index ) => {

                                    return(
                                        <Fragment key={ `externalLinks__${index}`}>

                                            {
                                                isVisible( links, props.visibility ) ?
                                                <div className={'ui-table-search-flyout-widget-with-advanced-options__external-link--link'} onClick = { () =>  externalLinksResolverResolver( { name: links.name, config: links, props: props } ) }>
                                                    { getLabel( links.title ) }
                                                </div> : null
                                            }

                                        </Fragment>
                                    );
                                } )
                            }

                        </div> : null
                    }
                    
                </div> : null
            }

            {/* table */}
            {
                ( props.disableDataView || isEmpty( searchQuery ) || ( props.isInitialData && 0 === props.items.length ) ) ? null : (
                    <div className={tableContainerClassName}>
                        <Table
                            visibility={props.visibility}
                            layoutConfig={props.layoutConfig}
                            isFetching={props.isFetching}
                            fetchingType={props.fetchingType}
                            disablePagination={props.disablePagination}

                            // data
                            searchQuery={searchQuery}
                            items={props.items}
                            columns={props.columns}
                            sortAndFilter={props.sortAndFilter}
                            pagination={props.pagination}
                            searchableColumns={props.searchableColumns}
                            actionsConfig={props.actionsConfig}
                            isInitialData={props.isInitialData}

                            // methods
                            onSortAndFilter={props.onSortAndFilter}
                            onPagination={props.onPagination}
                            onActionClick={props.onActionClick}
                        />
                    </div>
                )
            }
        </OutsideClickHandler>
    );
};

// set display name
tableSearchFlyoutWidgetWithAdvancedOptions.displayName = 'tableSearchFlyoutWidgetWithAdvancedOptions';

// set default props
tableSearchFlyoutWidgetWithAdvancedOptions.defaultProps = {
    isInitialData: true,
    isFetching: false,
    fetchingType: null,
    disablePagination: false,
    disableDataView: false,

    // static data
    visibility: {},
    widgetConfig: {},
    layoutConfig: {},
    searchableColumns: [],
    actionsConfig: {},

    // data
    items: [],
    columns: {
        primary: [],
        secondary: [],
    },
    filters: [],
    sortAndFilter: {},
    pagination: {},
    customizedRequestData: {},
    isIdentitySearchActive: false,
    identitySearchEnabled: false,

    // methods
    onFilterChange: noop,
    onSortAndFilter: noop,
    onPagination: noop,
    onActionClick: noop,
    onDataVisibilityChange: noop,
};

// prop types
tableSearchFlyoutWidgetWithAdvancedOptions.propTypes = {};
